<template>
  <div class="portal-wrap">
    <header :class="['appbar', compact && 'appbar--compact']">
      <router-link :to="homeLink" class="brand" aria-label="Edda Home">
        <span class="logo-wrap">
          <img :src="logoUrl" alt="Edda Home" class="logo" />
        </span>
        <span class="brand-block">
          <span class="brand-title">{{ brandTitle }}</span>
          <span :class="['mode-chip', inIncentives ? 'mode-chip--green' : 'mode-chip--red']">
            {{ headerTitle }}
          </span>
        </span>
      </router-link>

      <nav v-if="isAuth" class="nav">

        <!-- ================= INCENTIVES MODE (Sales + Installer) ================= -->
        <template v-if="inIncentives">
          <router-link :to="portalHomeRoute" class="navlink">← Back to Portal</router-link>
          <router-link :to="{ name: 'incentives-dashboard' }" class="navlink">Dashboard</router-link>
          <!-- <router-link :to="{ name: 'incentives-sales-new' }" class="navlink">Add Sale</router-link> -->
          <router-link :to="{ name: 'incentives-rewards' }" class="navlink">Rewards</router-link>

          <!-- <router-link :to="{ name: 'messages-inbox' }" class="navlink">
            Messages
            <span v-if="unreadCount > 0" class="msgbadge">{{ unreadCount }}</span>
          </router-link> -->

          <!-- Branch manager quick link -->
          <router-link
            v-if="role === 'branch_manager'"
            :to="{ name: 'branch-expenses' }"
            class="navlink"
          >Branch Expenses</router-link>

          <!-- Incentives Admin only for super_admin -->
          <router-link
            v-if="role === 'super_admin'"
            :to="{ name: 'admin' }"
            class="navlink"
          >Incentives Admin</router-link>

          <button class="link logout" @click="doLogout">Logout</button>
        </template>

        <!-- ================= SALES CRM ================= -->
        <template v-else-if="isSalesRole">
          <router-link :to="{ name: 'sales-dashboard' }" class="navlink">Dashboard</router-link>
          <router-link :to="{ name: 'sales-customers' }" class="navlink">Clients</router-link>
          <router-link :to="{ name: 'sales-quotations' }" class="navlink">Quotations</router-link>
          <router-link :to="{ name: 'sales-followups' }" class="navlink">Follow-ups</router-link>
          <router-link :to="{ name: 'sales-history' }" class="navlink">History</router-link>

          <router-link :to="{ name: 'messages-inbox' }" class="navlink">
            Messages
            <span v-if="unreadCount > 0" class="msgbadge">{{ unreadCount }}</span>
          </router-link>

          <router-link
            v-if="canAccessIncentives"
            :to="{ name: 'incentives-dashboard' }"
            class="navlink navlink--accent"
          >
            Incentives →
          </router-link>

          <router-link
            v-if="canSeeAdminCRM"
            :to="{ name: 'admin-crm' }"
            class="navlink"
          >
            {{ role === 'branch_manager' ? 'Branch' : 'Admin' }}
          </router-link>

          <button class="link logout" @click="doLogout">Logout</button>
        </template>

        <!-- ================= DISPATCH ================= -->
        <template v-else-if="role === 'dispatch'">
          <router-link :to="{ name: 'ops-dispatch' }" class="navlink">Dashboard</router-link>
          <router-link :to="{ name: 'ops-dispatch-history' }" class="navlink">History</router-link>

          <router-link :to="{ name: 'messages-inbox' }" class="navlink">
            Messages
            <span v-if="unreadCount > 0" class="msgbadge">{{ unreadCount }}</span>
          </router-link>

          <button class="link logout" @click="doLogout">Logout</button>
        </template>

        <!-- ================= INSTALLER ================= -->
        <template v-else-if="role === 'installer'">
          <router-link :to="{ name: 'ops-installer' }" class="navlink">Dashboard</router-link>
          <router-link :to="{ name: 'ops-installer-history' }" class="navlink">History</router-link>

          <router-link :to="{ name: 'messages-inbox' }" class="navlink">
            Messages
            <span v-if="unreadCount > 0" class="msgbadge">{{ unreadCount }}</span>
          </router-link>

          <router-link
            :to="{ name: 'incentives-dashboard' }"
            class="navlink navlink--accent"
          >
            Incentives
          </router-link>

          <button class="link logout" @click="doLogout">Logout</button>
        </template>

        <!-- ================= FINANCE ================= -->
        <template v-else-if="role === 'finance'">
          <router-link :to="{ name: 'ops-finance' }" class="navlink">Dashboard</router-link>
          <router-link :to="{ name: 'ops-finance-history' }" class="navlink">History</router-link>

          <router-link :to="{ name: 'messages-inbox' }" class="navlink">
            Messages
            <span v-if="unreadCount > 0" class="msgbadge">{{ unreadCount }}</span>
          </router-link>

          <button class="link logout" @click="doLogout">Logout</button>
        </template>

        <!-- ================= RM STORE ================= -->
        <template v-else-if="role === 'rm_store'">
          <router-link :to="{ name: 'ops-rm-dashboard' }" class="navlink">Dashboard</router-link>
          <router-link :to="{ name: 'ops-rm-incoming' }" class="navlink">Incoming</router-link>
          <router-link :to="{ name: 'ops-rm-outgoing' }" class="navlink">Outgoing</router-link>
          <button class="link logout" @click="doLogout">Logout</button>
        </template>



      </nav>
    </header>

    <main class="content">
      <router-view />
    </main>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  data () {
    return {
      logoUrl: require('@/assets/logo.png'),
      compact: false
    }
  },

  mounted () {
    this._onScroll = () => { this.compact = window.scrollY > 6 }
    window.addEventListener('scroll', this._onScroll, { passive: true })

    if (this.isAuthenticated) {
      this.$store.dispatch('messages/loadInbox')
    }
  },

  beforeDestroy () {
    window.removeEventListener('scroll', this._onScroll)
  },

  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'currentUser']),
    ...mapGetters('messages', ['unreadCount']),

    isAuth () { return this.isAuthenticated },

    role () {
      const u = this.currentUser || {}
      return u.role || ''
    },

    isSalesRole () {
      return ['sales_employee', 'branch_manager', 'branch_admin', 'super_admin'].includes(this.role)
    },

    canAccessIncentives () {
      return ['sales_employee', 'branch_manager', 'branch_admin', 'super_admin', 'installer'].includes(this.role)
    },

    canSeeAdminCRM () {
      return ['branch_manager', 'branch_admin', 'super_admin'].includes(this.role)
    },

    inIncentives () {
      if (!this.canAccessIncentives) return false
      return this.$route.matched.some(r => r.meta && r.meta.mode === 'incentives')
    },


    brandTitle () {
      if (this.role === 'dispatch' || this.role === 'installer' || this.role === 'rm_store') return 'Ops Command Center'
      if (this.role === 'finance') return 'Finance Command Center'
      return 'Sales Command Center'
    },

    headerTitle () {
      if (this.inIncentives) return 'Incentives'
      if (this.role === 'dispatch') return 'Dispatch Portal'
      if (this.role === 'installer') return 'Installer Portal'
      if (this.role === 'finance') return 'Finance Portal'
      if (this.role === 'rm_store') return 'RM Store Portal'
      return 'CRM'
    },

    homeLink () {
      if (this.inIncentives) return { name: 'incentives-dashboard' }

      if (this.role === 'dispatch') return { name: 'ops-dispatch' }
      if (this.role === 'installer') return { name: 'ops-installer' }
      if (this.role === 'finance') return { name: 'ops-finance' }
      if (this.role === 'rm_store') return { name: 'ops-rm-dashboard' }

      return { name: 'sales-dashboard' }
    },

    portalHomeRoute () {
      // Back button used in incentives header
      if (this.role === 'installer') return { name: 'ops-installer' }
      return { name: 'sales-dashboard' }
    }
  },

  watch: {
    '$route.path' () {
      if (this.isAuthenticated) this.$store.dispatch('messages/loadInbox')
    }
  },

  methods: {
    doLogout () {
      this.$store.dispatch('auth/logout')
      this.$router.push({ name: 'login' })
    }
  }
}
</script>



<style lang="scss">
:root {
  /* --- Light theme colors --- */
  --bg: #f9fafb;          /* overall background (slightly lighter) */
  --card: #ffffff;        /* card surfaces */
  --ink: #1f2937;         /* main text */
  --muted: #6b7280;       /* helper text */
  --accent: #22c55e;      /* green highlight for incentives chip */
  --warn: #f59e0b;        /* amber */
  --brand: #f50404;       /* primary red */
  --brand-600: #e00000;   /* deeper red for active states */
  --line: #e5e7eb;        /* soft borders */
  --blurbg: rgba(255,255,255,0.75);
}

.msgbadge{
  margin-left: 8px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 900;
  background: #ef4444;
  color: #fff;
}

/* --- Layout --- */
.portal-wrap {
  min-height: 100vh;
  background:
    radial-gradient(1200px 600px at 10% -20%, rgba(245,4,4,0.05), transparent 60%),
    radial-gradient(1000px 500px at 110% -10%, rgba(245,4,4,0.04), transparent 60%),
    var(--bg);
  color: var(--ink);
}

/* --- Premium Appbar --- */
.appbar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 14px 22px;
  backdrop-filter: saturate(180%) blur(8px);
  -webkit-backdrop-filter: saturate(180%) blur(8px);
  background:
    linear-gradient(180deg, var(--blurbg), rgba(255,255,255,0.9));
  border-bottom: 1px solid #eef2f7;

  /* Elegant brand underline in red */
  box-shadow:
    0 1px 0 0 rgba(0,0,0,0.04),
    inset 0 -2px 0 0 var(--brand);
  transition: padding 160ms ease, box-shadow 160ms ease, background 160ms ease;
}
.appbar--compact {
  padding: 8px 20px;
  box-shadow:
    0 1px 0 0 rgba(0,0,0,0.05),
    inset 0 -2px 0 0 var(--brand-600);
}

/* --- Branding --- */
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: var(--ink);
}
.logo-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  width: 140px; /* fits your rectangular logo */
  padding: 4px 10px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 12px;
  box-shadow:
    0 1px 1px rgba(0,0,0,0.04),
    0 0 0 4px rgba(245,4,4,0.06);
  transition: transform 0.25s ease, height 0.25s ease, width 0.25s ease;
  will-change: transform;
}

.appbar--compact .logo-wrap {
  transform: scale(0.94);
  height: 48px; /* fixed to prevent reflow */
  width: 140px;
}

/* LOGO IMAGE */
.logo {
  height: 100%;
  width: auto;
  max-height: 40px; /* consistent visual height */
  object-fit: contain;
  display: block;
  border-radius: 8px;
  transition: transform 0.25s ease;
  will-change: transform;
}

.appbar--compact .logo {
  transform: scale(0.95);
}

.brand-block {
  display:block;
  align-items: center;
  gap: 10px;
}
.brand-title {
  font-weight: 700;
  letter-spacing: 0.2px;
  font-size: 18px;
  font-family:'Lucida Sans', 'Lucida Sans Regular', 'Lucida Grande', 'Lucida Sans Unicode', Geneva, Verdana, sans-serif;
}
.mode-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--brand-600);
}
.mode-chip--red {
  background: linear-gradient(180deg, #fff, #fff);
  color: var(--brand-600);
  border-color: #fde2e2;
  box-shadow: 0 1px 0 rgba(245,4,4,0.06);
}
.mode-chip--green {
  background: linear-gradient(180deg, #fff, #fff);
  color: #15803d;
  border-color: #dcfce7;
  box-shadow: 0 1px 0 rgba(34,197,94,0.08);
}

/* --- Nav --- */
.nav {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* pill-like links with subtle hover + active underline indicator */
.navlink {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 10px;
  text-decoration: none;
  color: var(--ink);
  font-weight: 600;
  opacity: 0.95;
  transition: background 120ms ease, transform 120ms ease, box-shadow 120ms ease;
  border: 1px solid transparent;
}

.navlink:hover {
  background: #fff;
  border-color: #f3f4f6;
  box-shadow: 0 1px 0 rgba(0,0,0,0.03);
  transform: translateY(-0.5px);
}

.navlink.router-link-exact-active {
  background: #fff;
  border-color: #fde2e2;
  box-shadow:
    0 1px 0 rgba(0,0,0,0.03),
    0 0 0 3px rgba(245,4,4,0.08);
  color: var(--brand-600);
}

.navlink.router-link-exact-active::after {
  content: "";
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: -9px;
  height: 3px;
  border-radius: 2px;
  background: linear-gradient(90deg, var(--brand), #ff5c5c);
}

.navlink--accent {
  background: linear-gradient(180deg, #fff, #fff);
  border-color: #fde2e2;
}

/* logout link */
.link.logout {
  margin-left: 6px;
  background: linear-gradient(180deg, #fff, #fff);
  border: 1px solid #fee2e2;
  color: #b91c1c;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 700;
  transition: background 120ms ease, box-shadow 120ms ease;
}
.link.logout:hover {
  background: #fff5f5;
  box-shadow: 0 1px 0 rgba(185,28,28,0.06);
}

/* --- Main Content --- */
.content {
  width: 100%;
  max-width: none;   /* remove limit */
  padding: 20px 24px;
  margin: 0;
  background: var(--bg);
}

/* --- Cards (global style for consistency) --- */
.card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 16px;
  box-shadow:
    0 1px 2px rgba(0,0,0,0.04),
    0 8px 24px -18px rgba(245,4,4,0.35);
}

/* --- Helpers --- */
.helper {
  color: var(--muted);
}

/* --- Buttons --- */
.btn {
  background: linear-gradient(180deg, #fff, #fff);
  color: var(--brand-600);
  font-weight: 700;
  border: 1px solid #fde2e2;
  border-radius: 10px;
  padding: 9px 14px;
  cursor: pointer;
  transition: box-shadow 120ms ease, transform 120ms ease;
}
.btn:hover {
  box-shadow: 0 1px 0 rgba(0,0,0,0.03), 0 0 0 4px rgba(245,4,4,0.08);
  transform: translateY(-0.5px);
}
.btn.secondary {
  background: #ffffff;
  color: #1f2937;
  border: 1px solid #e5e7eb;
}
.btn.small {
  padding: 6px 10px;
  font-size: 12px;
}

/* --- Badges --- */
.badge--green { background: #16a34a; color: #fff; }
.badge--blue  { background: #1d4ed8; color: #fff; }
.badge--red   { background: #dc2626; color: #fff; }
.badge--amber { background: #f59e0b; color: #111; }
.badge--yellow{ background: #facc15; color: #111; }

/* --- Mobile tweaks --- */
@media (max-width: 820px) {
  .brand-title { display: none; } /* save space; chip + logo carry the brand */
  .content { padding: 16px; }
}
@media (max-width: 540px) {
  .nav { gap: 4px; }
  .navlink { padding: 7px 10px; border-radius: 8px; }
  .logo-wrap { width: 120px; height: 38px; padding: 5px 8px; }
}
@media (max-width: 420px) {
  .logo { height: 100%; }
}

/* -------------------- KEEP THESE INPUT STYLES EXACTLY AS PROVIDED -------------------- */
/* Light inputs across the portal */
.portal-wrap .input,
.portal-wrap input.input,
.portal-wrap select.input,
.portal-wrap textarea.input {
  background: #ffffff !important;
  color: #111827 !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 10px;
  padding: 10px 12px;
  height: 40px;
  outline: none;
}

.portal-wrap .input::placeholder { color: #9ca3af; }
.portal-wrap .input:focus {
  box-shadow: 0 0 0 3px rgba(225, 29, 46, 0.15);
  border-color: #fecaca;
}
</style>
